// Tool Palette — DocumentDetail action strip (EXPERIMENT / mockup
// + FR 2026-05-31 data-driven catalog).
//
// Renders inside the page-header's action row, in front of the
// primary action group (Watch / Download / Edit / Delete). The two
// groups are visually distinguished by wrapper background tint:
// `.action-group--palette` (lighter) for this palette,
// `.action-group--primary` (darker) for the primary actions.
//
// Catalog comes from two sources:
//   1. Hardcoded mockup buttons in lib/tool-palette-config.ts —
//      the 9 in-app actions whose UI exists but whose backend
//      wiring is still mockup-only.
//   2. Installed-tool actions from useToolActions() — manifest-
//      declared verb:'tab' and verb:'invoke' entries. Tab actions
//      ship working (open the tool URL in a new tab); invoke
//      actions are placeholders until the API proxy lands.
//
// Per-org visibility config in lib/tool-palette-config.ts uses
// composite keys for tool actions (`${toolSlug}:${actionKey}`) so
// the operator can hide individual tool buttons without re-installing.

import { useMemo } from 'react';
import { toast } from './Toast.js';
import { useSession } from '../hooks/useSession.js';
import { useToolActions, type PaletteAction } from '../hooks/useToolActions.js';
import {
  TOOL_PALETTE_BUTTONS,
  applyPaletteOrder,
  loadPaletteConfig,
  loadPaletteOrder,
  type ToolPaletteButton,
} from '../lib/tool-palette-config.js';

export interface ToolPaletteProps {
  /** Optional document identity context. The mockup doesn't read it
   *  yet, but a real implementation will pass `documentId` into each
   *  button's handler (re-run pipeline / open graph modal / etc.). */
  documentId?: string;
  /** When the operator clicks a button. v1 toasts "coming soon" for
   *  non-ready actions and opens a new tab for ready tab-actions;
   *  passing a handler in lets a future caller override per-button
   *  behavior without re-architecting the component. */
  onCommand?: (key: string) => void;
}

/** CTSK-121 — resolve a tab action's launch URL: the manifest URL
 *  plus the action's paramDefaults appended as query params. Keys the
 *  manifest URL already carries win (the author's explicit query
 *  string beats the defaults block); actions without paramDefaults
 *  return the URL untouched. Exported for the panel/tests. */
export function tabActionUrl(action: Pick<PaletteAction, 'url' | 'paramDefaults'>): string | undefined {
  if (!action.url) return action.url;
  const defaults = action.paramDefaults;
  if (!defaults || Object.keys(defaults).length === 0) return action.url;
  try {
    const u = new URL(action.url);
    for (const [k, v] of Object.entries(defaults)) {
      if (v == null || u.searchParams.has(k)) continue;
      u.searchParams.set(k, typeof v === 'string' ? v : JSON.stringify(v));
    }
    return u.toString();
  } catch {
    // Malformed URL survived install-time validation somehow — launch
    // it untouched rather than throwing at render time.
    return action.url;
  }
}

/** Convert a hardcoded mockup catalog entry into the unified
 *  PaletteAction shape so render code only walks one list. */
function mockupToPaletteAction(b: ToolPaletteButton): PaletteAction {
  return {
    key: b.key,
    label: b.label,
    description: b.description,
    icon: b.icon,
    verb: 'mockup',
    ready: b.ready,
    source: 'mockup',
  };
}

export function ToolPalette({ documentId, onCommand }: ToolPaletteProps) {
  const { session } = useSession();
  const orgSlug = session?.activeOrg?.slug ?? '';
  const { actions: toolActions } = useToolActions();

  // Per-org visibility config + operator-curated ordering. Composite
  // keys are addressed in the same maps as the simple hardcoded keys.
  const config = useMemo(() => loadPaletteConfig(orgSlug), [orgSlug]);
  const order = useMemo(() => loadPaletteOrder(orgSlug), [orgSlug]);

  // Build the merged catalog: mockup buttons first (FR-recommended
  // default order), then tool-contributed actions. Apply the saved
  // order (drag-to-reorder in Settings → Tool Palette), then drop
  // anything hidden in the visibility config.
  const visibleActions = useMemo<PaletteAction[]>(() => {
    const merged: PaletteAction[] = [];
    for (const b of TOOL_PALETTE_BUTTONS) merged.push(mockupToPaletteAction(b));
    for (const a of toolActions) merged.push(a);
    const ordered = applyPaletteOrder(merged, order);
    return ordered.filter((a) => config[a.key] !== false);
  }, [config, order, toolActions]);

  function handle(a: PaletteAction) {
    // Tab actions: open the tool's URL in a new window. The hook
    // resolves these to a known URL from the manifest; we never
    // toast for them. CTSK-121 — paramDefaults ride along as query
    // params (tabActionUrl merges them; manifest query keys win).
    if (a.verb === 'tab' && a.url) {
      window.open(tabActionUrl(a), '_blank', 'noopener,noreferrer');
      return;
    }
    if (onCommand) {
      onCommand(a.key);
      return;
    }
    // Mockup + not-yet-ready invoke actions toast. The documentId
    // is intentionally unused in the toast — wired through for the
    // real implementation later.
    void documentId;
    toast.info(`${a.label} — coming soon. This palette is a mockup; see FEATURE_REQUESTS.md.`);
  }

  if (visibleActions.length === 0) {
    // The operator disabled every button (or no installs + every
    // mockup hidden). Render nothing — the outer .action-group
    // wrapper still renders empty, which is fine.
    return null;
  }

  return (
    <div className="tool-palette" role="toolbar" aria-label="Document tools">
      {visibleActions.map((a) => (
        <PaletteButton key={a.key} action={a} onClick={() => handle(a)} />
      ))}
    </div>
  );
}

// ─── individual palette button ──────────────────────────────────

function PaletteButton({
  action,
  onClick,
}: {
  action: PaletteAction;
  onClick: () => void;
}) {
  const Icon = action.icon;
  // Tab actions render as <a target="_blank">. Native click handles
  // navigation; the tooltip shows the target URL so the operator
  // knows where they're being sent before they click.
  if (action.verb === 'tab' && action.url) {
    // CTSK-121 — the href carries the action's paramDefaults as query
    // params; the tooltip shows the resolved URL so the operator sees
    // exactly where (and with what) they're being sent.
    const launchUrl = tabActionUrl(action);
    return (
      <a
        href={launchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="tool-palette__btn"
        title={`${action.description ?? action.label}\n\n${launchUrl} (opens in new tab)`}
      >
        <Icon size={13} aria-hidden />
        <span>{action.label}</span>
      </a>
    );
  }
  const title = action.ready
    ? action.description ?? action.label
    : `${action.description ?? action.label}\n\nComing soon (mockup).`;
  return (
    <button
      type="button"
      className="tool-palette__btn"
      onClick={onClick}
      // aria-disabled (not disabled) so the button is still focusable
      // and screen readers can announce the "coming soon" tooltip.
      // Click still fires the toast — important for discovery.
      aria-disabled={!action.ready}
      title={title}
    >
      <Icon size={13} aria-hidden />
      <span>{action.label}</span>
    </button>
  );
}
